/**
 * 色阶对话框（Ctrl+L，参照 Photoshop「图像→调整→色阶」，破坏性）。
 * 输入黑场/白场/伽马 + 输出黑场/白场，实时预览，确定后一次性改活动图层像素。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { LevelsParams } from '../../engine/adjustments/lut';
import styles from './LevelsDialog.module.css';

export const DEFAULT_LEVELS_PARAMS: LevelsParams = {
  inBlack: 0,
  inWhite: 255,
  gamma: 1,
  outBlack: 0,
  outWhite: 255,
};

export interface LevelsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onApply: (params: LevelsParams) => void;
  /** 当前参数下的预览图（装配层用同一份 levelsLut 现算，不是另一套近似）。 */
  readonly previewUrl?: string | undefined;
  readonly onPreview?: ((params: LevelsParams) => void) | undefined;
}

export function LevelsDialog(props: LevelsDialogProps): JSX.Element {
  const [inBlack, setInBlack] = createSignal(DEFAULT_LEVELS_PARAMS.inBlack);
  const [inWhite, setInWhite] = createSignal(DEFAULT_LEVELS_PARAMS.inWhite);
  const [gamma, setGamma] = createSignal(DEFAULT_LEVELS_PARAMS.gamma);
  const [outBlack, setOutBlack] = createSignal(DEFAULT_LEVELS_PARAMS.outBlack);
  const [outWhite, setOutWhite] = createSignal(DEFAULT_LEVELS_PARAMS.outWhite);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  createEffect(() => {
    if (props.open) {
      setInBlack(DEFAULT_LEVELS_PARAMS.inBlack);
      setInWhite(DEFAULT_LEVELS_PARAMS.inWhite);
      setGamma(DEFAULT_LEVELS_PARAMS.gamma);
      setOutBlack(DEFAULT_LEVELS_PARAMS.outBlack);
      setOutWhite(DEFAULT_LEVELS_PARAMS.outWhite);
    }
  });

  const params = (): LevelsParams => ({
    inBlack: inBlack(),
    inWhite: inWhite(),
    gamma: gamma(),
    outBlack: outBlack(),
    outWhite: outWhite(),
  });

  createEffect(() => {
    if (!props.open) return;
    props.onPreview?.(params());
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="levels-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="色阶"
          data-testid="levels-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>色阶</h2>

          <Show when={props.previewUrl !== undefined}>
            <div class={styles.previewPanel}>
              <img class={styles.previewImage} src={props.previewUrl} alt="色阶预览" data-testid="levels-preview" />
            </div>
          </Show>

          <label class={styles.row}>
            <span class={styles.label}>输入黑场</span>
            <input
              type="range"
              min="0"
              max="254"
              value={inBlack()}
              data-testid="levels-in-black"
              onInput={(e) => setInBlack(Math.min(inWhite() - 1, Number(e.currentTarget.value)))}
            />
            <span class={styles.value}>{inBlack()}</span>
          </label>
          <label class={styles.row}>
            <span class={styles.label}>伽马</span>
            <input
              type="range"
              min="0.1"
              max="10"
              step="0.01"
              value={gamma()}
              data-testid="levels-gamma"
              onInput={(e) => setGamma(Number(e.currentTarget.value))}
            />
            <span class={styles.value}>{gamma().toFixed(2)}</span>
          </label>
          <label class={styles.row}>
            <span class={styles.label}>输入白场</span>
            <input
              type="range"
              min="1"
              max="255"
              value={inWhite()}
              data-testid="levels-in-white"
              onInput={(e) => setInWhite(Math.max(inBlack() + 1, Number(e.currentTarget.value)))}
            />
            <span class={styles.value}>{inWhite()}</span>
          </label>
          <label class={styles.row}>
            <span class={styles.label}>输出黑场</span>
            <input
              type="range"
              min="0"
              max="255"
              value={outBlack()}
              data-testid="levels-out-black"
              onInput={(e) => setOutBlack(Number(e.currentTarget.value))}
            />
            <span class={styles.value}>{outBlack()}</span>
          </label>
          <label class={styles.row}>
            <span class={styles.label}>输出白场</span>
            <input
              type="range"
              min="0"
              max="255"
              value={outWhite()}
              data-testid="levels-out-white"
              onInput={(e) => setOutWhite(Number(e.currentTarget.value))}
            />
            <span class={styles.value}>{outWhite()}</span>
          </label>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="levels-confirm"
              onClick={() => props.onApply(params())}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
